import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation, computed, effect, inject, input, signal } from '@angular/core';
import { BusinessSystem } from '@nha/enums';
import { IfHasEntitlementDirective } from '@nha/auth';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { NHAAlertBoxComponent } from '../alert-box/nha-alert-box.component';
import { NHAPopupComponent } from '../popup/popup.component';
import { NHASkeletonComponent } from '../skeleton/skeleton.component';
import { NHATestDirective } from '../../directives/test/test.directive';
import { NHANotificationSettingsDialogComponent } from './components/notification-settings-dialog/notification-settings-dialog.component';
import { formatRelativeTime } from './functions/relative-time.function';
import { playNotificationChime, primeNotificationChime } from './functions/notification-chime.function';
import { INotification } from './interfaces';
import { NotificationMenuStore } from './state';

@Component({
	selector: 'nha-notification-menu',
	templateUrl: './notification-menu.component.html',
	styleUrls: ['./notification-menu.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NHAPopupComponent,
		NHATestDirective,
		NHAAlertBoxComponent,
		NHASkeletonComponent,
		IfHasEntitlementDirective,
		MatBadgeModule,
		MatButtonModule,
		MatDialogModule,
		MatIconModule
	]
})
export class NHANotificationMenuComponent implements OnInit {
	private _store = inject(NotificationMenuStore);
	private _dialog = inject(MatDialog);

	/**
	 * Scopes which notifications and notification types are fetched. Defaults to myNHA so every
	 * suite app shows suite-wide notifications.
	 */
	businessSystemID = input<number>(BusinessSystem.MyNHA);

	notifications = this._store.notifications;
	isLoading = this._store.isLoading;
	isLoadingMore = this._store.isLoadingMore;
	hasError = this._store.hasError;
	hasMore = this._store.hasMore;

	/** "+" while rows the badge hasn't counted remain on the server, so the number reads as a floor. */
	badgeText = computed(() => `${this._store.unreadCount()}${this._store.unreadCountIsPartial() ? '+' : ''}`);
	showBadge = computed(() => this._store.unreadCount() > 0);
	canMarkAllRead = computed(() => !this.isLoading() && !this.hasError() && this.notifications().some((n) => !n.isRead));

	/** True while the bell's ring animation runs; cleared by animationend so it can replay. */
	isRinging = signal<boolean>(false);

	relativeTime = formatRelativeTime;

	constructor() {
		effect(() => {
			if (this._store.realtimePulse() === 0) {
				return;
			}

			this.isRinging.set(true);
			playNotificationChime();
		});
	}

	ngOnInit(): void {
		this._store.initialize(this.businessSystemID());

		// the chime is only ever triggered by a hub push, so audio has to be unlocked ahead of one
		primeNotificationChime();
	}

	ringAnimationDone(): void {
		this.isRinging.set(false);
	}

	sendTestNotification(): void {
		this._store.sendTestNotification();
	}

	openChanged(isOpen: boolean): void {
		if (isOpen) {
			this._store.refresh();
		}
	}

	notificationClicked(notification: INotification): void {
		this._store.markRead(notification);

		if (notification.deepLinkUrl) {
			window.open(notification.deepLinkUrl, '_blank');
		}
	}

	markAllRead(): void {
		this._store.markAllRead();
	}

	loadMore(): void {
		this._store.loadMore();
	}

	retry(): void {
		this._store.load();
	}

	openSettings(popup: NHAPopupComponent): void {
		popup.close();
		this._dialog.open(NHANotificationSettingsDialogComponent, { autoFocus: false, data: { businessSystemID: this.businessSystemID() } });
	}
}
